import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Phone, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import './Auth.css';

function ForgotPassword({ onBackToLogin }) {
  const [step, setStep] = useState(1); // 1: phone, 2: OTP, 3: new password
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  
  const { setupRecaptcha, clearRecaptcha, sendPasswordResetOTP, updatePassword } = useAuth();

  const resetForm = useCallback(() => {
    clearRecaptcha();
    setStep(1);
    setOtp('');
    setConfirmationResult(null);
    setError('');
    setResendTimer(0);
  }, [clearRecaptcha]);

  useEffect(() => {
    return () => {
      clearRecaptcha();
    };
  }, [clearRecaptcha]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Setup and render recaptcha
      const fullPhone = '+91' + phone;
      const recaptchaVerifier = await setupRecaptcha('recaptcha-container');
      const confirmation = await sendPasswordResetOTP(fullPhone, recaptchaVerifier);
      
      setConfirmationResult(confirmation);
      setStep(2);
      setResendTimer(30);
    } catch (err) {
      console.error('Send OTP error:', err);
      clearRecaptcha();
      
      if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/quota-exceeded') {
        setError('SMS quota exceeded. Please try again tomorrow.');
      } else if (err.message?.includes('billing')) {
        setError('Phone auth requires Firebase Blaze plan. Please contact admin.');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Verify OTP
      await confirmationResult.confirm(otp);
      setStep(3);
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please request a new one.');
        setStep(1);
        setOtp('');
      } else {
        setError('Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Update password in Firestore
      const fullPhone = '+91' + phone;
      await updatePassword(fullPhone, newPassword);
      
      setSuccess('Password reset successfully! Please login with your new password.');
      
      setTimeout(() => {
        onBackToLogin();
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    try {
      setError('');
      setLoading(true);
      clearRecaptcha();
      
      // Setup and render recaptcha
      const fullPhone = '+91' + phone;
      const recaptchaVerifier = await setupRecaptcha('recaptcha-container');
      const confirmation = await sendPasswordResetOTP(fullPhone, recaptchaVerifier);
      
      setConfirmationResult(confirmation);
      setResendTimer(30);
    } catch (err) {
      clearRecaptcha();
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      onBackToLogin();
    } else {
      resetForm();
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button className="auth-back-btn" onClick={handleBack}>
          <ArrowLeft size={18} />
          <span>{step === 1 ? 'Back to Login' : 'Back'}</span>
        </button>

        <div className="auth-header">
          <div className="auth-logo-container">
            <img src="/logo.png" alt="LatDiagnostics" className="auth-logo" />
          </div>
          <p className="auth-brand">LatDiagnostics</p>
          <p className="auth-tagline">Medical Diagnostic Platform</p>
        </div>

        <div className="auth-title">
          <h1>Reset Password</h1>
          <p>
            {step === 1 && 'Enter your mobile number to receive OTP'}
            {step === 2 && 'Enter the OTP sent to your mobile'}
            {step === 3 && 'Create a new password'}
          </p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <CheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleSendOTP} className="auth-form">
            <div className="form-group">
              <label>MOBILE NUMBER</label>
              <div className="phone-input-wrapper">
                <div className="country-code">
                  <span>IN +91</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                <input
                  type="tel"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  disabled={loading}
                  className="phone-input"
                  maxLength={10}
                />
              </div>
            </div>

            <div id="recaptcha-container"></div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={20} className="spin" />
                  Sending OTP...
                </>
              ) : (
                'Send OTP →'
              )}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="auth-form">
            <div className="otp-sent-message">
              <Phone size={20} />
              <span>OTP sent to +91 {phone}</span>
            </div>

            <div className="form-group">
              <label>ENTER OTP</label>
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                className="auth-input otp-input"
                maxLength={6}
                autoFocus
              />
            </div>

            <div id="recaptcha-container"></div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={20} className="spin" />
                  Verifying...
                </>
              ) : (
                'Verify OTP →'
              )}
            </button>

            <div className="resend-otp-section">
              {resendTimer > 0 ? (
                <span className="resend-timer">Resend OTP in {resendTimer}s</span>
              ) : (
                <button type="button" className="resend-otp-btn" onClick={handleResendOTP} disabled={loading}>
                  Resend OTP
                </button>
              )}
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword} className="auth-form">
            <div className="form-group">
              <label>NEW PASSWORD</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  className="auth-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>CONFIRM PASSWORD</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className="auth-input"
                />
              </div>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={20} className="spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password →'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
